import { z } from 'zod';

import {
  createPaginatedResponseSchema,
  PagedResultSchema,
} from './pagingSchema.ts';

export const serviceTypeEnum = z.enum(['Mobile', 'Static']);

export const RechargePolicyEnum = z.enum([
  'AnyDepot',
  'InitialDepot',
  'FinalDepot',
  'InitialOrFinal',
]);

export const ResourceAssetSchema = z.object({
  id: z.uuid(),
  type: z.string().nullish(),
  typeId: z.uuid(),
  code: z.string().nullish(),
  name: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  modifiedDateTime: z.string().nullish(),
  defaultDays: z.array(z.string()).nullish().optional(),
});

export const ResourceWithAssetSchema = z.object({
  assignedAssets: z.array(ResourceAssetSchema).nullish(),
  id: z.uuid(),
  name: z.string().nullish(),
  type: z.string().nullish(),
  code: z.string().nullish(),
  modifiedDateTime: z.string().nullish(),
  isActive: z.boolean().nullish(),
  defaultDays: z.array(z.string()).nullish().optional(),
});

export const ResourceWithAssetResponseSchema = createPaginatedResponseSchema(
  ResourceWithAssetSchema
);

export type ResourceWithAssetResponseModel = z.infer<
  typeof ResourceWithAssetResponseSchema
>;

export const RMCreateResourceAssetLink = z.object({
  serviceLocationId: z.string(),
  resourceId: z.uuid(),
  technicianAssetId: z.uuid(),
  vanAssetId: z.uuid().optional(),
  serviceType: serviceTypeEnum,
  dateOfService: z.string(),
});

export const RouteInstanceQuery = z.object({
  serviceLocationId: z.uuid(),
  dateOfService: z.string(),
  routeInstanceId: z.uuid(),
});

export const RMSearchAssetQuery = z.object({
  id: z.string().optional(),
  serviceLocationId: z.string().optional(),
  resourceId: z.uuid().optional(),
  technicianAssetId: z.uuid().optional(),
  vanAssetId: z.uuid().optional(),
  serviceType: serviceTypeEnum,
  dateOfService: z.string().optional(),
});

export const RMCreatedAssetResponse = RMSearchAssetQuery.extend({
  createdBy: z.string().optional(),
  modifiedBy: z.string().optional(),
  modifiedDate: z.string().optional(),
  createdDate: z.string().optional(),
});

export const RMAssetResponseListSchema = z.array(RMCreatedAssetResponse);

export const RMAssetListPaginatedResponseSchema = PagedResultSchema.extend({
  results: RMAssetResponseListSchema,
});

export const RMUpdateResourceAssetLink = RMCreateResourceAssetLink.pick({
  technicianAssetId: true,
  vanAssetId: true,
}).extend({
  id: z.string(),
});

export const AssetLinkResponseSettingsSchema = z
  .object({
    limits: z
      .object({
        maxDistance: z.number().optional(),
      })
      .optional(),
  })
  .optional();

export const AssetLinkTimeWindowSchema = z
  .object({
    startTime: z.date().optional(),
    endTime: z.date().optional(),
  })
  .optional();

export const AssetLinkCapacitySettingsSchema = z
  .object({
    measure1: z.number().optional(),
    measure2: z.number().optional(),
    measure3: z.number().optional(),
    measure4: z.number().optional(),
  })
  .optional();

export const AssetLinkRechargeSettingsSchema = z
  .object({
    allowRecharges: z.boolean().optional(),
    rechargeToCapacity: z.boolean().optional(),
    rechargePolicy: z.string().optional(),
  })
  .optional();

export const AssetLinkDaysOfTheWeekSchema = z
  .object({
    monday: z.boolean().optional(),
    tuesday: z.boolean().optional(),
    wednesday: z.boolean().optional(),
    thursday: z.boolean().optional(),
    friday: z.boolean().optional(),
    saturday: z.boolean().optional(),
    sunday: z.boolean().optional(),
  })
  .optional();

export const AssetLinkDaysOfTheWeekArraySchema = z.enum([
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]);

export const RMUpdateResourceAssetLinkResponse = z.object({
  id: z.string(),
  serviceLocationId: z.string(),
  resourceId: z.string(),
  technicianAssetId: z.string(),
  serviceType: z.string(),
  dateOfService: z.string(),
  createdBy: z.string(),
  modifiedBy: z.string(),
  modifiedDate: z.string(),
  createdDate: z.string(),
});

export const RouteSchema = z.object({
  id: z.uuid().optional(),
  serviceLocationId: z.uuid(),
  name: z.string(),
  description: z.string(),
  capabilities: z.string().array(),
  templateValidFrom: z.string().optional(),
  templateValidTo: z.string().optional(),
  createdBy: z.string().optional(),
  modifiedBy: z.string().optional(),
  modifiedDateTime: z.string().optional(),
  createdDateTime: z.string().optional(),
  settings: z
    .object({
      limits: z.object({
        maxDistance: z.number(),
      }),
    })
    .optional(),
  startTimeWindow: z.object({
    startTime: z.string(),
    endTime: z.string(),
  }),
  finishTimeWindow: z.object({
    startTime: z.string(),
    endTime: z.string(),
  }),
  serviceTimeWindow: z
    .object({
      startTime: z.string().optional(),
      endTime: z.string().optional(),
    })
    .optional(),
  capacitySettings: z.object({
    measure1: z.number(),
    measure2: z.number(),
    measure3: z.number(),
    measure4: z.number(),
  }),
  rechargeSettings: z
    .object({
      allowRecharges: z.boolean(),
      rechargeToCapacity: z.boolean(),
      rechargePolicy: RechargePolicyEnum,
    })
    .optional(),
  daysOfTheWeek: AssetLinkDaysOfTheWeekSchema,
  sameDay: z.boolean().optional(),
});

export const RouteInstanceSchema = z.object({
  id: z.uuid().optional(),
  serviceLocationId: z.uuid(),
  name: z.string(),
  description: z.string(),
  capabilities: z.string(),
  templateValidFrom: z.string().optional(),
  templateValidTo: z.string().optional(),
  createdBy: z.string().optional(),
  modifiedBy: z.string().optional(),
  modifiedDateTime: z.string().optional(),
  createdDateTime: z.string().optional(),
  maxDistance: z.number(),
  startTimeWindow: z.object({
    start: z.string(),
    end: z.string(),
  }),
  finishTimeWindow: z.object({
    start: z.string(),
    end: z.string(),
  }),
  serviceTimeWindow: z
    .object({
      start: z.string().optional(),
      end: z.string().optional(),
    })
    .optional(),
  capacitySettings: z.object({
    measure1: z.number(),
    measure2: z.number(),
    measure3: z.number(),
    measure4: z.number(),
  }),
  rechargeSettings: z
    .object({
      allowRecharges: z.boolean(),
      rechargeToCapacity: z.boolean(),
      rechargePolicy: z.string().or(z.number()),
    })
    .optional(),
});

export const RouteResponseSchema = PagedResultSchema.extend({
  results: z.array(RouteSchema),
});

export const RouteInstanceResponseSchema = PagedResultSchema.extend({
  results: z.array(RouteInstanceSchema),
});

export const RMAssetFormSchema = RouteInstanceSchema.extend({
  asset: z.uuid().optional(),
  technician: z.uuid().optional(),
});

export type RMSearchAssetQueryModel = z.infer<typeof RMSearchAssetQuery>;
export type RMCreatedAssetResponseModel = z.infer<
  typeof RMCreatedAssetResponse
>;
export type RMAssetListPaginatedResponseModel = z.infer<
  typeof RMAssetListPaginatedResponseSchema
>;
export type RMCreateResourceAssetLinkModel = z.infer<
  typeof RMCreateResourceAssetLink
>;
export type RMUpdateResourceAssetLinkModel = z.infer<
  typeof RMUpdateResourceAssetLink
>;
export type RMUpdateResourceAssetLinkResponseModel = z.infer<
  typeof RMUpdateResourceAssetLinkResponse
>;
export type RouteModel = z.infer<typeof RouteSchema>;
export type RouteResponseModel = z.infer<typeof RouteResponseSchema>;

export type RouteInstanceQueryModel = z.infer<typeof RouteInstanceQuery>;

export type RouteInstanceModel = z.infer<typeof RouteInstanceSchema>;
export type RouteInstanceResponseModel = z.infer<
  typeof RouteInstanceResponseSchema
>;
export type ServiceType = z.infer<typeof serviceTypeEnum>;

export type RMAssetFormType = z.infer<typeof RMAssetFormSchema>;
